import { useEffect, useState } from 'react';

export type CountdownValue = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

const ZERO: CountdownValue = { days: 0, hours: 0, minutes: 0, seconds: 0 };

const diffToCountdown = (diffMs: number): CountdownValue => {
  if (diffMs <= 0) return ZERO;

  const totalSeconds = Math.floor(diffMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return { days, hours, minutes, seconds };
};

/**
 * Hook de contagem regressiva até `targetDate` (string parseável por `Date`,
 * tipicamente ISO 8601 com timezone — não fazer conversão manual de fuso).
 *
 * SSR-safe: retorna `null` até montar no cliente (evita mismatch de
 * hidratação por depender de `Date.now()`). Depois de montar, atualiza a
 * cada 1s via `setInterval`. Quando `targetDate` já passou, retorna zeros e
 * não agenda mais nenhum interval (trava em zero).
 *
 * @param targetDate - data-alvo (ISO 8601) ou `undefined`/vazio.
 * @returns `{ days, hours, minutes, seconds }` ou `null` (ainda não montou
 *   no cliente, ou não há `targetDate`).
 */
export const useCountdown = (targetDate?: string): CountdownValue | null => {
  const [value, setValue] = useState<CountdownValue | null>(null);

  useEffect(() => {
    if (!targetDate) {
      setValue(null);
      return;
    }

    const target = new Date(targetDate).getTime();

    if (Number.isNaN(target)) {
      setValue(null);
      return;
    }

    const tick = () => {
      const diff = target - Date.now();
      setValue(diffToCountdown(diff));
      return diff;
    };

    const diff = tick();

    if (diff <= 0) {
      // Já expirou: mostra zeros e não agenda mais nenhum interval.
      return;
    }

    const intervalId = setInterval(tick, 1000);

    return () => clearInterval(intervalId);
  }, [targetDate]);

  return value;
};

export default useCountdown;
